import WarningIcon from "@mui/icons-material/Warning";
import { Alert, Typography, useMediaQuery, useTheme } from "@mui/material";
import api from "app/api";
import { ROLES } from "app/constants";
import useAccess from "features/profile/useAccess";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";

// Type for the API response - events that are over online limit but not closed
type OverLimitEventItem = {
  id: number;
  start_time: string; // event_group.start_time
  program_name: string; // program.name
};

const GlobalOnlineLimitAlert: React.FC = () => {
  const { hasPermissions, hasRoles } = useAccess();
  const [events, setEvents] = useState<OverLimitEventItem[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const hasPermissionsRef = useRef(hasPermissions);
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("sm"));

  // Keep refs in sync
  useEffect(() => {
    hasPermissionsRef.current = hasPermissions;
  }, [hasPermissions]);

  // API call to fetch events over online limit
  const fetchAvailableEvents = useCallback(async () => {
    if (
      !hasPermissionsRef.current("booking.create") ||
      isPollingRef.current ||
      hasRoles(ROLES.PARTNER)
    ) {
      return;
    }

    // Only fetch if tab is active
    if (document.visibilityState !== "visible") {
      // Schedule next check when tab becomes visible or after timeout
      timeoutRef.current = setTimeout(fetchAvailableEvents, 20000);
      return;
    }

    isPollingRef.current = true;

    try {
      const { success, data } = await api.get("/events/over-online-limit");
      if (success && data && Array.isArray(data)) {
        setEvents(data);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error("Failed to fetch over-limit events:", error);
      setEvents([]);
    } finally {
      isPollingRef.current = false;
      // Schedule next poll after current one completes
      if (hasPermissionsRef.current("booking.create")) {
        timeoutRef.current = setTimeout(fetchAvailableEvents, 20000);
      }
    }
  }, []); // Empty dependency array - function is stable

  // Set up polling when component mounts or permissions change
  useEffect(() => {
    if (hasPermissions("booking.create")) {
      // Start initial fetch
      fetchAvailableEvents();
    }

    // Handle visibility change - resume polling when tab becomes active
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        hasPermissionsRef.current("booking.create")
      ) {
        // Clear any existing timeout and start fresh when tab becomes visible
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        fetchAvailableEvents();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup timeout and event listener on unmount or permission change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      isPollingRef.current = false;
    };
  }, []); // Empty dependency array - fetchAvailableEvents is stable

  // Don't render if no permissions or no events
  if (!hasPermissions("booking.create") || events.length === 0) {
    return null;
  }

  // Group events by start time for more compact display
  const groupedEvents = events.reduce((acc, event) => {
    const time = moment(event.start_time).format("L LT");
    if (!acc[time]) {
      acc[time] = [];
    }
    acc[time].push(event.program_name);
    return acc;
  }, {} as Record<string, string[]>);

  // Create a simple list of all events with times
  const eventsList = Object.entries(groupedEvents)
    .map(([time, programs]) => `${time}: ${programs.join(", ")}`)
    .join("; ");

  return (
    <Alert
      severity="warning"
      variant="filled"
      icon={<WarningIcon />}
      sx={{
        borderRadius: 0,
        mb: 0,
        mx: mdDown ? 0 : -3, // Same negative margin pattern as WebSocketStatusBanner
      }}
    >
      <Typography variant="body2">
        <strong>Online limit túllépés!</strong> {events.length} esemény zárásra
        vár: {eventsList}
      </Typography>
    </Alert>
  );
};

export default GlobalOnlineLimitAlert;
